import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  pageToFirst,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit, DropdownEdit } from "components/table/inputs"
import { DropdownField } from "components/table/display"
// Controls
import Controls from "components/user-admin/controls/"
// Data
import { alphanum } from "components/table/lib/sort"
import api_match from "data/interface/api"
import carouselService from "data/carousel"
import keyService, * as chord from "data/key"
import portfolioService from "data/portfolio"
import userGroupService from "data/user-groups"
// Styles
import { UserTable_s } from "components/user-admin/user-styles"
import usersService from "data/user"

const UserTable = (props: {tableId: string, data: object[]}) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }
  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [portfolios, setPortfolios] = useState<object[]>([])
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('user')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const portfolioSubscription = portfolioService.stream().subscribe(data => {
      api_match(
        data,
        init  => setPortfolios(init.value),
        ok    => {
          setPortfolios(ok.value)
        },
        err   => {
          setPortfolios(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const userGroupSubscription = userGroupService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "USERGROUP_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            userGroupService.create()
          }
          break
      }
    })

    portfolioService.fetchPortfolios()

    return function cleanup() {
      carouselSubscription.unsubscribe()
      portfolioSubscription.unsubscribe()
      userGroupSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  const persist = (value, field, rowData) => {
    let user = _.cloneDeep(rowData);
    _.set(user, field, value);
    usersService.updateUser(user);
    if (value) carouselService.updateRowData(user);
  }

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  const data = props.data.sort((a, b) => alphanum(a['email'], b['email']))

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <UserTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Email"
            {...field('email')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Full Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "120px"}} className="p-editable-column"
            header="Enabled"
            {...field('enabled', (rowData, props) => DropdownField({options: [{value: true, label: 'True'},{value: false, label: 'False'}], rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={[{value: true, label: 'True'},{value: false, label: 'False'}]} persist={persist} {...props} />}
          />
          <Column style={{minWidth: "200px"}} className="p-editable-column"
            header="Application Access"
            {...field('approles', (rowData, props) => { return(
              <CarouselTrigger
                field="approles"
                rowData={rowData}
                title="Application Access"
                persist={persist}
                controls={{single: {control: Controls.ApplicationAccess, name: 'Select Application Access'}}}
                hover={{
                  detailed: {list: {label: 'Application Access', key: 'approles'}}
                }}
                componentAtRest={()=>{
                  if (rowData['approles']?.length > 1){
                    return 'Detailed'
                  } else if (rowData['approles']?.length > 0) {
                    return rowData['approles']
                  } else {
                    return 'Select Application Access'
                  }
                }}
              />)},
              (err) => !_.includes(['code', 'name'], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="email" data={carouselData} key="carousel-modal" width={600}/> }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </UserTable_s>
    </div>
  )
}

export default UserTable
